import get from 'lodash/get';
import * as React from 'react';
import { HotKeys } from 'react-hotkeys';
import { VisibilitySensor } from 'ui/common';
import { getOptionsOrDefault } from 'ui/common/code-table/get-options-or-default';
import { ElementLoader } from 'ui/common/element-loader/element-loader';
import { InputControlContext } from 'ui/common/input-control-context';
import { keyCodeTypes } from 'ui/common/key-code-types';
import { SliderReadonly } from 'ui/common/slider-readonly/slider-readonly';
import { ComplexValue, SingleKeyValueComponent } from 'ui/common/types';
import { SliderCaption } from './slider-caption';
import { SlidingBar } from './sliding-bar';
import { SlidingCircle } from './sliding-circle';
import * as utils from './utils';
import styles from './slider-rating.scss';
import { ResizeObserver } from 'ui/slider-rating/ResizeObserver';

export interface SliderRatingProps extends SingleKeyValueComponent<number> {
  innerStyle: string;
  useOptionsColors: boolean;
  compactModeTreshold: number;
  sortOptionsDirection: 'asc' | 'desc';
  scheduleUpdate?: Function;
  locale?: string;
}
export interface SliderState{
  centers: number[];
  initialValue: number;
  /**
   * When a value is hovered when sliding, the caption is updated
   * before onChange is triggered
   */
  nextActivePosition?: number;
}

export class SliderRating extends React.Component<SliderRatingProps, SliderState> {

  private keyMap: {[key:string]: string[]};
  private handlers: {[shortcut:string]: (e:any)=>void };

  constructor(props: SliderRatingProps) {
    super(props);

    this.state = {
      centers: [],
      initialValue: 0
    };

    this.keyMap = {
      slideLeft: [keyCodeTypes.ArrowLeft],
      slideRight: [keyCodeTypes.ArrowRight]
    };

    this.containerRef = React.createRef<HTMLDivElement>();

    this.handlers = {
      slideLeft: event => {
        event.preventDefault();
        const activeIndex = this.activeElementIndex();
        this.handleChange(activeIndex - 1);
      },
      slideRight: event => {
        event.preventDefault();
        const activeIndex = this.activeElementIndex();
        this.handleChange(activeIndex + 1);
      }
    };
  }

  public static defaultProps?: Partial<SliderRatingProps> = {
    innerStyle: '',
    useOptionsColors: true,
    compactModeTreshold: 4,
    sortOptionsDirection: 'desc'
  };
  private circleWidth: number = 20;
  private currentWidth: number = 0;
  private containerRef:React.RefObject<HTMLDivElement>;
  private forceUpdateWhenWidthIsReady: boolean = false;

  private updateWidth () {
    const ref = this.containerRef.current;
    if (ref) {
      const newWidth = this.currentWidth !== ref.offsetWidth;
      this.currentWidth = ref.offsetWidth;
      if (this.forceUpdateWhenWidthIsReady || newWidth) {
        this.forceUpdateWhenWidthIsReady = false;
        this.generateSegmentsMetadata();
      }
    }
  }

  public componentDidUpdate(){
    this.updateWidth();
  }

  private activeElementIndex = () => {
    const elementsToRender = getOptionsOrDefault(
      this.props.valueItem,
      this.props.options
    );
    if (this.props.valueItem) {
      return elementsToRender.findIndex(
        e => e.value === this.props.valueItem.value
      );
    } else {
      return elementsToRender.findIndex(e => e.value === 0);
    }
  };

  private generateSegmentsMetadata(elements?:any) {
    const elementsToRender = elements || getOptionsOrDefault(
      this.props.valueItem,
      this.props.options
    );

    if (this.currentWidth) {
      const centers = utils.calculateSegmentCenters(
        this.currentWidth,
        elementsToRender.length,
        -this.circleWidth);

      // TODO: don't use local state
      // The source of most bugs in this component is using state
      // for calculation results, instead of memoizing calculating functions
      this.setState({
        centers: centers
      });
    } else if(elementsToRender.length !== 0) {
      // in the case if the data is ready, but the component have not found a width.
      // Set a flag that is used in updateWidth when the width is set.
      this.forceUpdateWhenWidthIsReady = true;
    }
  }

  private delayedUpdate (callback:Function) {
    if (callback) {
      if (this.props.scheduleUpdate) {
        this.props.scheduleUpdate(callback);
      } else {
        callback();
      }
    }
  }

  private handleBarClicked = (e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
    const {centers} = this.state;
    const {changedTouches} = e as React.TouchEvent<HTMLElement>;
    const {pageX} = e as React.MouseEvent<HTMLElement>;

    const xOffset = pageX || changedTouches[0].pageX;
    const closestCenter = utils.findClosestCenter(
      centers,
      xOffset -
        this.containerRef.current.getBoundingClientRect().left -
        this.circleWidth
    );
    const index = centers.findIndex( x => x === closestCenter);
    if(Number.isFinite(index)){
      this.delayedUpdate( () => this.handleChange(index) );
    }
  };

  private handleChange = (option: number) => {
    const {valueItem, options, onChange} = this.props;
    const selectedItem = getOptionsOrDefault(valueItem, options)[option];
    selectedItem && onChange(selectedItem);
  };

  private handleUpdatedCaption = (option: number) => {
    this.setState({nextActivePosition: option});
  }

  private onResize = () => {
    this.updateWidth();
    this.generateSegmentsMetadata();
  };

  private loadOptions = () => {
    const { options, onLoadOptions } = this.props;
    const needsOptions = !options || !options.length;

    if (onLoadOptions && needsOptions) {
      this.props.onLoadOptions({ sort: this.props.sortOptionsDirection });
    }
  };

  public componentDidMount() {
    this.forceUpdateWhenWidthIsReady = true;
    this.updateWidth();
  }

  public UNSAFE_componentWillReceiveProps(props: SliderRatingProps) {
    if (this.props.valueItem && get(this.props, 'valueItem.value') !== get(props, 'valueItem.value') ) {
      this.setState({nextActivePosition: undefined});
    }
    if(props && this.props && props.options != null) {
      const newElementsToRender = getOptionsOrDefault(
        props.valueItem,
        props.options
      );

      const prevElementsToRender = getOptionsOrDefault(
        this.props.valueItem,
        this.props.options
      );
      if ( prevElementsToRender.length !== newElementsToRender.length || newElementsToRender.length !== this.state.centers.length ) {
        this.generateSegmentsMetadata(newElementsToRender);
        return;
      }
    }

    if ( !this.props.options && props.options ) {
      this.generateSegmentsMetadata();
    }
  }

  private centerEqual (a:number[], b:number[]) {
    if (!a || !b || a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]){
        return false;
      }
    }
    return true;
  }

  public shouldComponentUpdate (nextProps:SliderRatingProps, nextState:SliderState) {
    const {options, valueItem, readOnly} = this.props;
    const {options:nextOptions, valueItem:nextValue, readOnly:nextReadOnly} = nextProps;
    const {centers, nextActivePosition} = this.state;
    const {centers:nextCenters, nextActivePosition:nextAP} = nextState;
    const {validation} = nextProps;

    if (
      validation === this.props.validation &&
      get(options, 'length') === get(nextOptions, 'length') &&
      get(valueItem, 'value') === get(nextValue, 'value') &&
      nextActivePosition === nextAP &&
      this.centerEqual(centers, nextCenters) &&
      readOnly === nextReadOnly) {
      
      return false;
    }

    return true;
  }

  private hasColors = (items: ComplexValue<number>[]) => items.some(item => !!item.backgroundColor);

  public render() {
    const {
      valueItem,
      options,
      useOptionsColors,
      compactModeTreshold,
      readOnly,
      scheduleUpdate,
      locale
    } = this.props;
    const {
      centers,
      nextActivePosition
    } = this.state;
    if (
      !options ||
      !options.length ||
      !centers
      ) {
        return  <div className={styles.containerSpacer} ref={this.containerRef}>
                  <VisibilitySensor onShow={this.loadOptions}>
                    <ElementLoader />
                  </VisibilitySensor>
                </div>;
    }

    const elementsToRender = getOptionsOrDefault(
      valueItem,
      options
      );
      if (elementsToRender.length === 0) {
        return <InputControlContext {...this.props} />;
      }
    const activeIndex = Number.isFinite(nextActivePosition) && nextActivePosition >= 0 ? nextActivePosition : this.activeElementIndex();
    const classNames =
      useOptionsColors && this.hasColors(elementsToRender)
        ? `${styles.box} ${styles.inverse}`
        : styles.box;
    const breakStyle =
      compactModeTreshold < elementsToRender.length
        ? styles.break
        : '';

    const rtlDetect = require('rtl-detect');
    const isRtl = rtlDetect.isRtlLang(locale);

    const slider = (
      <div
        className={`${classNames} ${breakStyle}`}
        ref={this.containerRef}
        onClick={this.handleBarClicked}
      >

        <HotKeys keyMap={this.keyMap} handlers={this.handlers}>
          <SliderCaption
            bars={elementsToRender}
            active={activeIndex}
            isRtl={isRtl}
          />
          <div className={styles.boundary} tabIndex={0}>
            <SlidingBar
              bars={elementsToRender}
              active={activeIndex}
              isRtl={isRtl}
            />
            <SlidingCircle
              componentRef={this.containerRef.current}
              active={activeIndex}
              centers={centers}
              handleChange={this.handleChange}
              updateActiveOption={this.handleUpdatedCaption}
              scheduleUpdate={scheduleUpdate}
            />
          </div>
          <SliderCaption
            bars={elementsToRender}
            active={activeIndex}
            isRtl={isRtl}
          />
        </HotKeys>
      </div>
    );

    return (
      <InputControlContext {...this.props}>
        <ResizeObserver onResize={this.onResize}>
          {readOnly ? (
            <SliderReadonly valueItem={this.props.valueItem} />
            ) : (
              slider
              )}
        </ResizeObserver>
      </InputControlContext>
    );
  }
}
